import {fillArray, noop} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {ActionIcon, createStyles, Group, Text} from '@mantine/core';
import React, {useMemo} from 'react';

import {pluralize} from 'BenchlingUtil';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import SimpleTableEditable, {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {RowData} from 'notebook/tables/constants/RowConstants';
import {ColumnConstraint} from 'notebook/tables/constants/TableColumnConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import {
  BaseCondition,
  ConditionValidationState,
  isPersistedCondition,
} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';
import {MAX_PLAN_CONDITION_COUNT} from 'studies/typed/shared/procedure-runs/execution-status/useMethodExecutionLimitState';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {DEFAULT_CONDITION_VALIDATION_STATE} from './conditionUtils';
import {EditConditionDescriptionModal} from './EditConditionDescriptionModal';
import {
  buildConditionGroupsFromTableUpdate,
  buildConditionsFromTableUpdate,
  confirmDeleteConditions,
  dispatchByConditionShape,
  getDescErrorTriangle,
  getNameErrorTriangle,
  makeValueCell,
  resolveConditionName,
  resolveReplicateCount,
} from './replicateTableUtils';
import {useConditionDescriptionDraft} from './useConditionDescriptionDraft';

interface PDRowData extends RowData {
  // Keeps each transposed row tied to its original condition index for update/delete mapping.
  processDevelopmentCustomIndex?: number;
}

function makeInitialReplicateTable(
  tableName: string,
  replicatesPerCondition: Array<number>,
  namesPerCondition: Array<string>,
  descriptionsPerCondition: Array<string | undefined>,
  validationStatePerCondition: Array<ConditionValidationState>,
  isLockedPerCondition: Array<boolean>,
  locked: boolean,
  procedureType: ProcedureType,
  openEditDescriptionModal: (conditionIndex: number) => void,
  classes: ReturnType<typeof useStyles>['classes'],
  maxRows?: number
): Table {
  const makeNameCell = (text: string, index: number): Cell => ({
    text,
    locked: locked || isLockedPerCondition[index],
    align: 'left',
    bold: false,
    userAnnotationIndicator: getNameErrorTriangle(validationStatePerCondition, index, procedureType),
  });

  const makeDescCell = (text: string, index: number): Cell => {
    const isCellLocked = locked || isLockedPerCondition[index];
    return {
      text,
      locked: isCellLocked,
      align: 'left',
      bold: false,
      userAnnotationIndicator: getDescErrorTriangle(validationStatePerCondition, index, procedureType),
      formattedValue: (
        <div className={classes.descriptionCellContent}>
          <span
            className={classes.descriptionCellText}
            data-ellipsized="true"
            {...getTestProps({component: 'TransposedReplicateTable', element: 'descriptionText'})}
          >
            {text}
          </span>
          {!isCellLocked ? (
            <ActionIcon
              size="xs"
              variant="ghost"
              aria-label="Edit description"
              {...getTestProps({component: 'TransposedReplicateTable', element: 'editDescriptionButton'})}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                openEditDescriptionModal(index);
              }}
            >
              <BenchlingIcon icon="edit" className={classes.editDescriptionIcon} />
            </ActionIcon>
          ) : null}
        </div>
      ),
    };
  };

  const numConditions = replicatesPerCondition.length;
  const conditionRows = fillArray(numConditions, (conditionIndex) => [
    makeNameCell(namesPerCondition[conditionIndex], conditionIndex),
    makeValueCell(replicatesPerCondition[conditionIndex], conditionIndex, locked, isLockedPerCondition),
    makeDescCell(descriptionsPerCondition[conditionIndex] ?? '', conditionIndex),
  ]);

  return {
    name: tableName,
    widths: [100, 45, 95],
    rowData: fillArray(
      numConditions,
      (conditionIndex): PDRowData => ({processDevelopmentCustomIndex: conditionIndex})
    ),
    columnLabels: ['Name', 'Reps', 'Description'],
    columnConstraints: fillArray(
      3,
      (): ColumnConstraint => ({
        editLabelDisabled: true,
      })
    ),
    headerCol: false,
    headerRow: false,
    table: conditionRows,
    maxRows,
    lockedDims: {allowAddRows: !locked, allowRemoveRows: !locked, allowEditCols: false},
    allowRowRemoval: !locked,
    allowAddRows: !locked,
    allowColumnEdits: false,
    _hideRename: true,
  };
}

/**
 * Update conditions when any individual cell is updated.
 *
 * @package exported for testing.
 */
export function updateTableCells<TCondition extends BaseCondition>(
  table: Table,
  conditions: Array<TCondition>,
  setConditions: (conditions: Array<TCondition>, prevIndexes: Array<number | null>) => void,
  procedureType: ProcedureType,
  newConditionCoveredMethodIds?: ReadonlyArray<string>
): void {
  const originalValueIndexes = table.table.map(
    (_row, index) => (table.rowData?.[index] as PDRowData)?.processDevelopmentCustomIndex ?? null
  );

  let previousReplicateCount = 1;
  const newConditions = table.table.map((row, index) => {
    const name = row[0].text;
    const repsText = row[1].text;
    const originalIndex = originalValueIndexes[index];
    const isNewRow = originalIndex == null;
    const originalCondition = originalIndex == null ? undefined : conditions[originalIndex];

    // Default a new blank row from the preceding table row so consecutive pasted rows carry forward
    // the most recently specified replicate count.
    const isEmptyReplicates = repsText == null || repsText === '';
    const numReplicatesCell = {
      ...row[1],
      text: isNewRow && isEmptyReplicates ? Math.max(1, previousReplicateCount).toString() : repsText,
    };
    const numReplicates = resolveReplicateCount(numReplicatesCell, isNewRow, originalCondition);
    previousReplicateCount = numReplicates;

    return {
      numReplicates,
      // Assign default condition name for newly added rows
      name: resolveConditionName(name, index, procedureType),
      description: row[2].text,
    };
  });
  const updated = dispatchByConditionShape(
    conditions,
    (cs) => buildConditionsFromTableUpdate({newConditions, originalValueIndexes, conditions: cs}),
    (cs) =>
      buildConditionGroupsFromTableUpdate({
        newConditions,
        originalValueIndexes,
        conditions: cs,
        newConditionCoveredMethodIds,
      })
  );
  setConditions(updated as unknown as Array<TCondition>, originalValueIndexes);
}

interface TransposedReplicateTableProps<TCondition extends BaseCondition> {
  conditions: Array<TCondition>;
  setConditions: (conditions: Array<TCondition>, prevIndexes: Array<number | null>) => void;
  experimentalInputs: ExperimentalInputs | null;
  setExperimentalInputs: (experimentalInputs: ExperimentalInputs) => void;
  editNumColsButtons: JSX.Element;
  locked: boolean;
  procedureType: ProcedureType;
  newConditionCoveredMethodIds?: ReadonlyArray<string>;
  isPlan?: boolean;
  dataTestAttrs?: {[key: string]: string};
}

/**
 * Renders process development condition settings in a table layout where each row corresponds to one
 * condition. Columns are name, number of replicates, and description.
 */
export function TransposedReplicateTable<TCondition extends BaseCondition>(
  props: TransposedReplicateTableProps<TCondition>
): JSX.Element {
  const {classes} = useStyles();
  const isPlan = props.isPlan;
  const isLockedPerCondition = isPlan
    ? props.conditions.map(() => false)
    : props.conditions.map((item) => isPersistedCondition(item));
  const {
    isEditDescriptionModalOpen,
    descriptionDraft,
    setDescriptionDraft,
    openEditDescriptionModal,
    closeEditDescriptionModal,
    saveDescriptionDraft,
  } = useConditionDescriptionDraft({
    conditions: props.conditions,
    setConditions: props.setConditions,
  });

  const table = makeInitialReplicateTable(
    getProcedureRunConditionTextForProcedureType(props.procedureType, {capital: true, plural: true}),
    props.conditions.map((item) => item.numReplicates),
    props.conditions.map((item) => item.name),
    props.conditions.map((item) => item.description),
    props.conditions.map((item) => item.validationState ?? DEFAULT_CONDITION_VALIDATION_STATE),
    isLockedPerCondition,
    props.locked,
    props.procedureType,
    openEditDescriptionModal,
    classes,
    isPlan ? MAX_PLAN_CONDITION_COUNT : undefined
  );
  const hasAtLeastOneUnlockedRow = !props.locked && isLockedPerCondition.some((item) => !item);
  const totalReplicates = useMemo(
    () => props.conditions.reduce((acc, condition) => acc + condition.numReplicates, 0),
    [props.conditions]
  );

  const conditionWord = getProcedureRunConditionTextForProcedureType(props.procedureType);
  const conditionCountText = `${props.conditions.length} ${pluralize(
    props.conditions.length,
    conditionWord
  )}, ${totalReplicates} ${pluralize(totalReplicates, 'replicate')}`;
  const rightHeaderControl = !props.locked ? props.editNumColsButtons : undefined;

  function handleDeleteRows(
    index: number,
    start: number,
    end: number,
    doDelete: (index: number, start: number, end: number) => void,
    isProcedureRunPlan: boolean
  ): void {
    const conditionsToRemove = props.conditions.slice(start, end + 1);
    confirmDeleteConditions({
      unitWord: 'row',
      conditionsToRemove,
      experimentalInputs: props.experimentalInputs,
      procedureType: props.procedureType,
      isProcedureRunPlan,
      doDelete: () => {
        doDelete(index, start, end);
      },
    });
  }

  return (
    <div
      className={classes.container}
      {...getTestProps({component: 'TransposedReplicateTable', element: 'root'})}
    >
      <Group position="apart" noWrap={true} className={classes.header}>
        <div className={classes.headerLeft}>
          <Text className={classes.conditionsLabel}>Conditions</Text>
          <Text className={classes.countLabel}>{conditionCountText}</Text>
        </div>
        {rightHeaderControl}
      </Group>
      <div className={classes.tableWrapper}>
        <SimpleTableEditable
          {...standaloneProps}
          dataTestAttrs={props.dataTestAttrs}
          hideFormulaBox={true}
          isCompactBottomSpacing={true}
          canAddRemoveColumns={false}
          locked={props.locked}
          table={table}
          cid="doe-replicates-transposed"
          updateHandler={(_, {table: nextTable}: {table: Table}) => {
            updateTableCells(
              nextTable,
              props.conditions,
              props.setConditions,
              props.procedureType,
              props.newConditionCoveredMethodIds
            );
          }}
          storeState={noop}
          restoreState={noop}
          isCollapsible={false}
          suppressTitleBar={true}
          hideRowCount={true}
          onDeleteRows={(index, start, end, doDelete) => {
            handleDeleteRows(index, start, end, doDelete, isPlan ?? false);
          }}
        />
      </div>
      <EditConditionDescriptionModal
        opened={isEditDescriptionModalOpen}
        descriptionDraft={descriptionDraft}
        onClose={closeEditDescriptionModal}
        onDescriptionDraftChange={setDescriptionDraft}
        onSave={saveDescriptionDraft}
      />
      {hasAtLeastOneUnlockedRow ? (
        <Text className={classes.pasteHelperText}>
          {`Paste from a spreadsheet to add multiple ${getProcedureRunConditionTextForProcedureType(
            props.procedureType,
            {plural: true}
          )}`}
        </Text>
      ) : null}
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  container: {
    width: '100%',
  },
  header: {
    marginTop: theme.spacing.md,
    marginBottom: theme.spacing.xs,
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xxs,
  },
  conditionsLabel: {
    color: theme.black,
    fontWeight: 600,
    fontSize: theme.fontSizes.md,
    lineHeight: '20px',
  },
  countLabel: {
    color: theme.colors.gray[6],
    fontSize: theme.fontSizes.xs,
    lineHeight: '16px',
  },
  tableWrapper: {
    '& .mediocre-tableEditable-container': {
      padding: 0,
    },
  },
  descriptionCellContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xxs,
    width: '100%',
    minWidth: 0,
  },
  descriptionCellText: {
    minWidth: 0,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  editDescriptionIcon: {
    color: Color.GRAY_700,
  },
  pasteHelperText: {
    marginTop: theme.spacing.xs,
    color: theme.colors.gray[6],
    fontSize: theme.fontSizes.xs,
    lineHeight: '14px',
  },
}));
